//biome-ignore-all lint/suspicious/noExplicitAny: because
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityLogDto,
  CreateCustomFieldDto,
  CustomFieldDto,
  GetAllParamsDto,
  GetCustomFieldsResponseDto,
  GetCustomFieldUsageResponseDto,
  UpdateCustomFieldDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';

export type GetCustomFieldsParamsDto = GetAllParamsDto & {
  excludePriorityFrameworkFields?: boolean;
};

export const useCustomFieldsApi = (api: AxiosInstance) => {
  const baseUrl = `/customFields`;
  const defaultLimit = 25;
  const maxLimit = 100; // Bigger batches when all custom fields are needed upfront instead of on-demand scrolling

  const createCustomField = async (
    customField: CreateCustomFieldDto,
  ): Promise<{ data: CustomFieldDto }> => {
    const { data } = await api.post(baseUrl, customField);
    return data;
  };

  const getCustomFields = async (
    params?: GetCustomFieldsParamsDto,
  ): Promise<GetCustomFieldsResponseDto> => {
    const { data } = await api.get(baseUrl, { params: { ...params } });
    return data;
  };

  const getCustomFieldsByIds = async (ids: number[]): Promise<{ data: CustomFieldDto[] }> => {
    const { data } = await api.get(`${baseUrl}/getByIds`, {
      params: { ids },
      paramsSerializer: {
        indexes: null, // To get ids=1&ids=2&ids=3 (no brackets)
      },
    });
    return data;
  };

  const getCustomField = async (id?: number): Promise<{ data: CustomFieldDto | undefined }> => {
    if (id === undefined || id === -1) return { data: undefined };

    const { data } = await api.get(`${baseUrl}/${id}`);
    return data;
  };

  const getCustomFieldUsage = async (
    id?: number,
    includeDeleted?: boolean,
  ): Promise<{ data: GetCustomFieldUsageResponseDto | undefined }> => {
    if (id === undefined || id === -1) return { data: undefined };

    const { data } = await api.get(`${baseUrl}/${id}/usage`, { params: { includeDeleted } });
    return { data };
  };

  const cloneCustomField = async (id: number): Promise<{ data: CustomFieldDto }> => {
    const { data } = await api.post(`${baseUrl}/${id}/clone`);
    return data;
  };

  const updateCustomField = async (
    updatedCustomField: UpdateCustomFieldDto & { id: number },
  ): Promise<{ data: CustomFieldDto }> => {
    const { id, ...fieldData } = updatedCustomField;
    const { data } = await api.put(`${baseUrl}/${id}`, fieldData);
    return data;
  };

  const archiveCustomField = async (id: number) => {
    const { data } = await api.put(`${baseUrl}/${id}/archive`);
    return data;
  };

  const restoreCustomField = async (id: number) => {
    const { data } = await api.put(`${baseUrl}/${id}/restore`);
    return data;
  };

  const deleteCustomField = async (id: number) => {
    const { data } = await api.delete(`${baseUrl}/${id}`);
    return data;
  };

  const getCustomFieldActivity = async (id: number) => {
    const { data } = await api.get(`${baseUrl}/${id}/activity-logs`);

    const userIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(api, userIds, data, (item: any) => item.userId);

    return result;
  };

  const queryClient = useQueryClient();

  const useGetAll = (params?: GetCustomFieldsParamsDto, enabled?: boolean) => {
    const limit = params?.limit ?? defaultLimit;
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: [baseUrl, 'GetAll', params?.lifecycleFilter, params],
      enabled: enabled ?? true,
      queryFn: ({ pageParam }) => {
        const newParams = { ...params, limit, offset: pageParam * limit };
        return getCustomFields(newParams);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined;
      },
    });
  };

  const useGetSome = (ids: number[]) =>
    useQuery({
      queryKey: [baseUrl, ids],
      queryFn: () => getCustomFieldsByIds(ids),
      enabled: ids && ids.length > 0,
    });

  const useGetOne = (id?: number) =>
    useQuery({ queryKey: [baseUrl, id], queryFn: () => getCustomField(id) });
  const useGetOneUsage = (id?: number, includeDeleted?: boolean, enabled?: boolean) =>
    useQuery({
      queryKey: [baseUrl, id, includeDeleted],
      queryFn: () => getCustomFieldUsage(id, includeDeleted),
      enabled: id !== undefined && enabled,
    });

  const useGetCustomFieldActivityLog = (id: number) =>
    useQuery({
      queryKey: [baseUrl, id, 'activity-logs'],
      queryFn: () => getCustomFieldActivity(id),
    });

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createCustomField,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });
  const useClone = () =>
    useErrorHandlingMutation({
      mutationFn: cloneCustomField,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });
  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: updateCustomField,
      onSuccess: async (data) => {
        queryClient.setQueryData([baseUrl, data.data.id], () => data);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl], type: 'all' }),
          queryClient.invalidateQueries({ queryKey: ['/feedback-forms'] }),
        ]);
      },
    });
  const useArchive = () =>
    useErrorHandlingMutation({
      mutationFn: archiveCustomField,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });
  const useRestore = () =>
    useErrorHandlingMutation({
      mutationFn: restoreCustomField,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });
  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteCustomField,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });

  return {
    useCreate,
    useClone,
    useUpdate,
    useGetOne,
    useGetSome,
    useGetOneUsage,
    useGetCustomFieldActivityLog,
    useGetAll,
    useArchive,
    useRestore,
    useDelete,
    maxLimit,
  };
};
